import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

export interface Livestream {
  id: string;
  creatorProfileId: string;
  title: string;
  description?: string;
  streamUrl?: string;
  playbackUrl?: string;
  thumbnailUrl?: string;
  status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
  scheduledStartAt?: string;
  startedAt?: string;
  endedAt?: string;
  viewerCount: number;
  peakViewerCount: number;
  isPublic: boolean;
  tierIds: string[];
  createdAt: string;
  updatedAt: string;
  creatorProfile?: {
    id: string;
    displayName: string;
    user: {
      id: string;
      username: string;
      avatar?: string;
    };
  };
}

export interface CreateLivestreamPayload {
  title: string;
  description?: string;
  scheduledStartAt?: string;
  isPublic?: boolean;
  tierIds?: string[];
  streamUrl?: string;
  playbackUrl?: string;
  thumbnailUrl?: string;
}

export const livestreamService = {
  async getLivestreams(creatorId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/livestreams/creator/${creatorId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    return res.data as { livestreams: Livestream[] };
  },

  async getLivestream(id: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/livestreams/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    return res.data as { livestream: Livestream };
  },

  async getMyLivestreams() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/livestreams/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { livestreams: Livestream[] };
  },

  async createLivestream(payload: CreateLivestreamPayload, thumbnail?: File) {
    const token = localStorage.getItem('accessToken');
    const formData = new FormData();
    
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => formData.append(`${key}[]`, String(v)));
        } else {
          formData.append(key, String(value));
        }
      }
    });

    if (thumbnail) {
      formData.append('thumbnail', thumbnail);
    }

    const res = await axios.post(`${API_BASE_URL}/api/livestreams`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data as { livestream: Livestream };
  },

  async updateLivestream(id: string, payload: Partial<CreateLivestreamPayload> & { status?: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED' }, thumbnail?: File) {
    const token = localStorage.getItem('accessToken');
    const formData = new FormData();
    
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => formData.append(`${key}[]`, String(v)));
        } else {
          formData.append(key, String(value));
        }
      }
    });

    if (thumbnail) {
      formData.append('thumbnail', thumbnail);
    }

    const res = await axios.put(`${API_BASE_URL}/api/livestreams/${id}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data as { livestream: Livestream };
  },

  async deleteLivestream(id: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.delete(`${API_BASE_URL}/api/livestreams/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { message: string };
  },

  async startLivestream(id: string, streamUrl?: string, playbackUrl?: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/livestreams/${id}/start`, {
      streamUrl,
      playbackUrl
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { livestream: Livestream };
  },

  async endLivestream(id: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/livestreams/${id}/end`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { livestream: Livestream };
  },

  async updateViewerCount(id: string, viewerCount: number) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/livestreams/${id}/viewer-count`, {
      viewerCount
    }, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    return res.data as { message: string };
  },
};

